/**
 * Four Nordic — cart drawer, quote drawer and predictive search.
 * Depends on: window.theme.moneyFormat, window.routes (set in theme.liquid) and rfq.js
 * for the quote list storage. Progressive: every entry point still works without JS.
 */
(function () {
  'use strict';

  var RFQ_KEY = 'four-nordic-rfq-v1';
  var root = document.querySelector('[data-fn-drawers]');
  var lastFocus = null;
  var quoteOpenPending = false;

  /* ── helpers ──────────────────────────────────────────────────────────── */

  function money(cents) {
    var format = (window.theme && window.theme.moneyFormat) || '{{amount}}';
    var value = Number(cents || 0) / 100;

    function withDelimiters(decimals, thousands, precision) {
      var fixed = value.toFixed(precision);
      var parts = fixed.split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return precision > 0 ? whole + decimals + parts[1] : whole;
    }

    return format.replace(/\{\{\s*(\w+)\s*\}\}/g, function (match, name) {
      switch (name) {
        case 'amount': return withDelimiters('.', ',', 2);
        case 'amount_no_decimals': return withDelimiters('.', ',', 0);
        case 'amount_with_comma_separator': return withDelimiters(',', '.', 2);
        case 'amount_no_decimals_with_comma_separator': return withDelimiters(',', '.', 0);
        case 'amount_with_space_separator': return withDelimiters(',', ' ', 2);
        case 'amount_no_decimals_with_space_separator': return withDelimiters(',', ' ', 0);
        case 'amount_with_apostrophe_separator': return withDelimiters('.', "'", 2);
        default: return withDelimiters('.', ',', 2);
      }
    });
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (typeof text === 'string') node.textContent = text;
    return node;
  }

  function label(name, fallback) {
    if (!root) return fallback;
    return root.getAttribute('data-' + name) || fallback;
  }

  function sizedImage(url, width) {
    if (!url) return '';
    return url.replace(/(\.(?:png|jpe?g|webp|gif))(\?.*)?$/i, '_' + width + 'x$1$2');
  }

  /* ── drawer shell ─────────────────────────────────────────────────────── */

  function openDrawer(name) {
    if (!root) return;
    lastFocus = document.activeElement;
    root.setAttribute('data-open', name);
    document.documentElement.classList.add('fn-no-scroll');
    var panel = root.querySelector('[data-fn-drawer="' + name + '"]');
    if (panel) {
      var focusable = panel.querySelector('button, a, input');
      if (focusable) focusable.focus();
    }
  }

  function closeDrawer() {
    if (!root) return;
    root.removeAttribute('data-open');
    document.documentElement.classList.remove('fn-no-scroll');
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  /* ── cart ─────────────────────────────────────────────────────────────── */

  function setCartCount(count) {
    document.querySelectorAll('[data-fn-cart-count]').forEach(function (node) {
      node.textContent = String(count);
      node.hidden = false;
    });
    if (window.theme) window.theme.cartCount = count;
  }

  function renderCart(cart) {
    var list = root && root.querySelector('[data-fn-cart-lines]');
    var empty = root && root.querySelector('[data-fn-cart-empty]');
    var foot = root && root.querySelector('[data-fn-cart-foot]');
    var total = root && root.querySelector('[data-fn-cart-total]');
    var countNode = root && root.querySelector('[data-fn-cart-drawer-count]');
    if (!list) return;

    setCartCount(cart.item_count);
    while (list.firstChild) list.removeChild(list.firstChild);

    cart.items.forEach(function (item) {
      var line = el('div', 'fn-drawer__line');
      line.setAttribute('data-line-key', item.key);

      var media = el('a', 'fn-drawer__media');
      media.href = item.url;
      if (item.image) {
        var img = el('img', 'fn-drawer__image');
        img.src = sizedImage(item.image, 160);
        img.alt = '';
        img.loading = 'lazy';
        media.appendChild(img);
      }
      line.appendChild(media);

      var body = el('div', 'fn-drawer__body');
      if (item.sku) body.appendChild(el('span', 'fn-drawer__sku', item.sku));
      var title = el('a', 'fn-drawer__title', item.product_title);
      title.href = item.url;
      body.appendChild(title);
      if (item.variant_title && item.variant_title.toLowerCase() !== 'default title') {
        body.appendChild(el('span', 'fn-drawer__meta', item.variant_title));
      }

      var controls = el('div', 'fn-drawer__controls');
      var qty = el('div', 'fn-drawer__qty');
      [['-1', '\u2212'], ['1', '+']].forEach(function (pair, index) {
        var button = el('button', 'fn-drawer__qty-button', pair[1]);
        button.type = 'button';
        button.setAttribute('data-fn-cart-step', pair[0]);
        if (index === 0) {
          qty.appendChild(button);
          qty.appendChild(el('span', 'fn-drawer__qty-value', String(item.quantity)));
        } else {
          qty.appendChild(button);
        }
      });
      controls.appendChild(qty);
      controls.appendChild(el('span', 'fn-drawer__price', money(item.final_line_price)));
      body.appendChild(controls);

      var remove = el('button', 'fn-drawer__remove', label('remove-label', 'Remove'));
      remove.type = 'button';
      remove.setAttribute('data-fn-cart-remove', '');
      body.appendChild(remove);

      line.appendChild(body);
      list.appendChild(line);
    });

    if (empty) empty.hidden = cart.item_count > 0;
    if (foot) foot.hidden = cart.item_count === 0;
    if (total) total.textContent = money(cart.total_price);
    if (countNode) {
      countNode.textContent = cart.item_count === 1
        ? label('one-item-label', '1 item')
        : String(cart.item_count) + ' ' + label('items-label', 'items');
    }
  }

  function fetchCart(open) {
    return fetch((window.routes && window.routes.cartUrl ? window.routes.cartUrl : '/cart') + '.js', {
      headers: { 'Accept': 'application/json' },
      credentials: 'same-origin'
    })
      .then(function (response) { return response.json(); })
      .then(function (cart) {
        renderCart(cart);
        if (open) openDrawer('cart');
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        return cart;
      });
  }

  function addToCart(form, button) {
    var body = new FormData(form);
    if (button) button.classList.add('is-loading');

    return fetch('/cart/add.js', {
      method: 'POST',
      body: body,
      headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin'
    })
      .then(function (response) {
        if (!response.ok) return response.json().then(function (error) { throw error; });
        return response.json();
      })
      .then(function () { return fetchCart(true); })
      .catch(function (error) {
        var message = (error && (error.description || error.message)) || label('error-label', 'Could not add to cart');
        var toast = document.querySelector('[data-rfq-toast]');
        if (toast) {
          toast.textContent = message;
          toast.classList.add('is-visible');
          window.setTimeout(function () { toast.classList.remove('is-visible'); }, 2600);
        }
      })
      .then(function () { if (button) button.classList.remove('is-loading'); });
  }

  function changeLine(key, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      body: JSON.stringify({ id: key, quantity: quantity }),
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin'
    })
      .then(function (response) { return response.json(); })
      .then(function (cart) { renderCart(cart); });
  }

  /* ── quote list ───────────────────────────────────────────────────────── */

  function readQuote() {
    try {
      var items = JSON.parse(window.localStorage.getItem(RFQ_KEY) || '[]');
      return Array.isArray(items) ? items : [];
    } catch (error) {
      return [];
    }
  }

  function writeQuote(items) {
    try { window.localStorage.setItem(RFQ_KEY, JSON.stringify(items)); } catch (error) { /* private mode */ }
    document.querySelectorAll('[data-rfq-count]').forEach(function (node) {
      node.textContent = String(items.length);
      node.hidden = items.length === 0;
    });
    document.dispatchEvent(new CustomEvent('four-nordic:quote-updated', { detail: { items: items, source: 'drawer' } }));
  }

  function renderQuote(items) {
    var list = root && root.querySelector('[data-fn-quote-lines]');
    var empty = root && root.querySelector('[data-fn-quote-empty]');
    var foot = root && root.querySelector('[data-fn-quote-foot]');
    var countNode = root && root.querySelector('[data-fn-quote-drawer-count]');
    if (!list) return;

    while (list.firstChild) list.removeChild(list.firstChild);

    items.forEach(function (item) {
      var line = el('div', 'fn-drawer__line fn-drawer__line--quote');
      line.setAttribute('data-quote-id', item.id);

      var body = el('div', 'fn-drawer__body');
      if (item.sku) body.appendChild(el('span', 'fn-drawer__sku', item.sku));
      if (item.url) {
        var link = el('a', 'fn-drawer__title', item.title || item.reference);
        link.href = item.url;
        body.appendChild(link);
      } else {
        body.appendChild(el('span', 'fn-drawer__title', item.title || item.reference));
      }

      var controls = el('div', 'fn-drawer__controls');
      var qty = el('div', 'fn-drawer__qty');
      var minus = el('button', 'fn-drawer__qty-button', '\u2212');
      minus.type = 'button';
      minus.setAttribute('data-fn-quote-step', '-1');
      var plus = el('button', 'fn-drawer__qty-button', '+');
      plus.type = 'button';
      plus.setAttribute('data-fn-quote-step', '1');
      qty.appendChild(minus);
      qty.appendChild(el('span', 'fn-drawer__qty-value', String(item.quantity || 1)));
      qty.appendChild(plus);
      controls.appendChild(qty);
      body.appendChild(controls);

      var remove = el('button', 'fn-drawer__remove', label('remove-label', 'Remove'));
      remove.type = 'button';
      remove.setAttribute('data-fn-quote-remove', '');
      body.appendChild(remove);

      line.appendChild(body);
      list.appendChild(line);
    });

    if (empty) empty.hidden = items.length > 0;
    if (foot) foot.hidden = items.length === 0;
    if (countNode) {
      countNode.textContent = items.length === 1
        ? label('one-part-label', '1 part')
        : String(items.length) + ' ' + label('parts-label', 'parts');
    }
  }

  /* ── predictive search ────────────────────────────────────────────────── */

  function initSuggest(field) {
    var input = field.querySelector('input[type="search"]');
    var panel = field.querySelector('[data-fn-suggest]');
    if (!input || !panel) return;

    var timer = null;
    var controller = null;

    function hide() {
      panel.hidden = true;
      while (panel.firstChild) panel.removeChild(panel.firstChild);
    }

    function render(data, term) {
      var products = (data.resources && data.resources.results && data.resources.results.products) || [];
      var pages = (data.resources && data.resources.results && data.resources.results.pages) || [];
      var collections = (data.resources && data.resources.results && data.resources.results.collections) || [];

      while (panel.firstChild) panel.removeChild(panel.firstChild);

      if (!products.length && !pages.length && !collections.length) {
        panel.appendChild(el('p', 'fn-suggest__empty', label('no-results-label', 'No matches')));
        panel.hidden = false;
        return;
      }

      if (products.length) {
        panel.appendChild(el('span', 'fn-suggest__label', label('products-label', 'Products')));
        products.forEach(function (product) {
          var row = el('a', 'fn-suggest__item');
          row.href = product.url;

          var media = el('span', 'fn-suggest__media');
          if (product.featured_image && product.featured_image.url) {
            var img = el('img', 'fn-suggest__image');
            img.src = sizedImage(product.featured_image.url, 120);
            img.alt = '';
            media.appendChild(img);
          }
          row.appendChild(media);

          var body = el('span', 'fn-suggest__body');
          var sku = (product.variants && product.variants[0] && product.variants[0].sku) || product.sku;
          if (sku) body.appendChild(el('span', 'fn-suggest__sku', sku));
          body.appendChild(el('span', 'fn-suggest__title', product.title));
          row.appendChild(body);

          if (product.price) {
            row.appendChild(el('span', 'fn-suggest__price', money(Math.round(parseFloat(product.price) * 100))));
          }
          panel.appendChild(row);
        });
      }

      collections.concat(pages).forEach(function (item) {
        var row = el('a', 'fn-suggest__item fn-suggest__item--plain');
        row.href = item.url;
        row.appendChild(el('span', 'fn-suggest__title', item.title));
        panel.appendChild(row);
      });

      var all = el('a', 'fn-suggest__all', label('view-all-label', 'See all results') + ' \u2192');
      all.href = (window.routes && window.routes.searchUrl ? window.routes.searchUrl : '/search') +
        '?type=product&q=' + encodeURIComponent(term);
      panel.appendChild(all);
      panel.hidden = false;
    }

    function query(term) {
      if (controller) controller.abort();
      controller = typeof AbortController === 'function' ? new AbortController() : null;

      var url = (window.routes && window.routes.predictiveSearchUrl ? window.routes.predictiveSearchUrl : '/search/suggest')
        + '.json?q=' + encodeURIComponent(term)
        + '&resources[type]=product,collection,page'
        + '&resources[limit]=6'
        + '&resources[options][unavailable_products]=last'
        + '&resources[options][fields]=title,product_type,variants.title,variants.sku,vendor,tag';

      fetch(url, {
        headers: { 'Accept': 'application/json' },
        credentials: 'same-origin',
        signal: controller ? controller.signal : undefined
      })
        .then(function (response) { return response.json(); })
        .then(function (data) { render(data, term); })
        .catch(function () { /* aborted or offline — keep the panel as is */ });
    }

    input.setAttribute('autocomplete', 'off');
    input.addEventListener('input', function () {
      var term = input.value.trim();
      window.clearTimeout(timer);
      if (term.length < 2) { hide(); return; }
      timer = window.setTimeout(function () { query(term); }, 180);
    });

    input.addEventListener('keydown', function (event) {
      var items = Array.prototype.slice.call(panel.querySelectorAll('a'));
      if (event.key === 'Escape') { hide(); return; }
      if (!items.length || panel.hidden) return;

      var index = items.indexOf(document.activeElement);
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        (items[index + 1] || items[0]).focus();
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        (items[index - 1] || items[items.length - 1]).focus();
      }
    });

    panel.addEventListener('keydown', function (event) {
      var items = Array.prototype.slice.call(panel.querySelectorAll('a'));
      var index = items.indexOf(document.activeElement);
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        (items[index + 1] || input).focus();
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        (index <= 0 ? input : items[index - 1]).focus();
      } else if (event.key === 'Escape') {
        hide();
        input.focus();
      }
    });

    document.addEventListener('click', function (event) {
      if (!field.contains(event.target)) hide();
    });
  }

  /* ── wiring ───────────────────────────────────────────────────────────── */

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form.matches || !form.matches('form[action*="/cart/add"]')) return;
    if (form.hasAttribute('data-fn-no-ajax')) return;
    event.preventDefault();
    addToCart(form, form.querySelector('[type="submit"]'));
  });

  // rfq.js stores the item; we only need to know a click started it, so the
  // drawer opens for an add but not for edits made inside the quote page.
  document.addEventListener('click', function (event) {
    if (event.target.closest && event.target.closest('[data-rfq-add-product], [data-rfq-add-manual]')) {
      quoteOpenPending = true;
    }
  }, true);

  document.addEventListener('four-nordic:quote-updated', function (event) {
    var items = (event.detail && event.detail.items) || readQuote();
    renderQuote(items);
    if (quoteOpenPending && event.detail && event.detail.source !== 'drawer') {
      quoteOpenPending = false;
      openDrawer('quote');
    }
  });

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target.closest) return;

    var open = target.closest('[data-fn-open-drawer]');
    if (open) {
      event.preventDefault();
      var name = open.getAttribute('data-fn-open-drawer');
      if (name === 'cart') fetchCart(true); else { renderQuote(readQuote()); openDrawer('quote'); }
      return;
    }

    if (target.closest('[data-fn-close-drawer]')) {
      event.preventDefault();
      closeDrawer();
      return;
    }

    var step = target.closest('[data-fn-cart-step]');
    if (step) {
      var line = step.closest('[data-line-key]');
      var current = parseInt(line.querySelector('.fn-drawer__qty-value').textContent, 10) || 1;
      changeLine(line.getAttribute('data-line-key'), Math.max(0, current + parseInt(step.getAttribute('data-fn-cart-step'), 10)));
      return;
    }

    if (target.closest('[data-fn-cart-remove]')) {
      var removeLine = target.closest('[data-line-key]');
      changeLine(removeLine.getAttribute('data-line-key'), 0);
      return;
    }

    var quoteStep = target.closest('[data-fn-quote-step]');
    if (quoteStep) {
      var quoteLine = quoteStep.closest('[data-quote-id]');
      var delta = parseInt(quoteStep.getAttribute('data-fn-quote-step'), 10);
      var items = readQuote().map(function (item) {
        if (item.id !== quoteLine.getAttribute('data-quote-id')) return item;
        item.quantity = Math.max(1, (parseInt(item.quantity, 10) || 1) + delta);
        return item;
      });
      writeQuote(items);
      renderQuote(items);
      return;
    }

    if (target.closest('[data-fn-quote-remove]')) {
      var id = target.closest('[data-quote-id]').getAttribute('data-quote-id');
      var kept = readQuote().filter(function (item) { return item.id !== id; });
      writeQuote(kept);
      renderQuote(kept);
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && root && root.hasAttribute('data-open')) closeDrawer();
  });

  /* ── sticky header metrics ────────────────────────────────────────────── */

  function initStickyHeader() {
    var topbar = document.querySelector('.fn-topbar');
    var header = document.querySelector('.fn-header');
    if (!header) return;

    function measure() {
      var topbarHeight = topbar ? Math.round(topbar.getBoundingClientRect().height) : 0;
      var headerHeight = Math.round(header.getBoundingClientRect().height);
      document.documentElement.style.setProperty('--fn-topbar-h', topbarHeight + 'px');
      document.documentElement.style.setProperty('--fn-header-h', headerHeight + 'px');
      return topbarHeight;
    }

    var offset = measure();

    function onScroll() {
      var stuck = window.pageYOffset > offset;
      header.classList.toggle('is-stuck', stuck);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', function () { offset = measure(); onScroll(); }, { passive: true });
    if (typeof ResizeObserver === 'function') {
      new ResizeObserver(function () { offset = measure(); }).observe(header);
    }
    onScroll();
  }

  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('[data-fn-search-field]').forEach(initSuggest);
    initStickyHeader();
    renderQuote(readQuote());
    if (window.theme && window.theme.cartCount) setCartCount(window.theme.cartCount);
  });
})();
